/**
 * JOURNAL ENGINE — Double-Entry Accounting
 * All financial transactions must flow through this engine.
 * Every entry creates a matching debit and credit.
 * 
 * Account Code Reference:
 * 1101 Cash at Bank       2101 Supplier Payables   3100 Share Capital
 * 1201 Trade Receivables  2201 Loans Payable        3200 Retained Earnings
 * 1220 Shareholder Rec.   2401 Accrued Interest     3300 Capital Contributions
 * 1310 Raw Materials      2402 Accrued Salaries     4101-4105 Revenue
 * 1320 WIP                                          5100-5600 COGS
 * 1330 Finished Goods                               6100-6900 Opex
 * 1610 CWIP               
 * 1650 Accum. Depreciation
 */

import { supabase } from './supabase'

// Generate a unique journal ID
const genJournalId = (prefix) => {
  const ts = Date.now().toString(36).toUpperCase()
  return `JNL-${prefix}-${ts}`
}

// Ensure an account code exists in chart_of_accounts
const ensureAccount = async (code, name, category, normalBalance) => {
  await supabase.from('chart_of_accounts').upsert({
    code, name, category,
    subcategory: category === 'Asset' ? 'Non-Current' : category,
    normal_balance: normalBalance,
    is_active: true,
  }, { onConflict: 'code', ignoreDuplicates: true })
}


// Account code registry — auto-inserted into COA when first used
const KNOWN_ACCOUNTS = {
  '1101': ['Cash at Bank (Absa)', 'Asset', 'Debit'],
  '1201': ['Trade Receivables', 'Asset', 'Debit'],
  '1220': ['Shareholder Receivable', 'Asset', 'Debit'],
  '1310': ['Raw Materials', 'Asset', 'Debit'],
  '1320': ['Work in Progress', 'Asset', 'Debit'],
  '1330': ['Finished Goods', 'Asset', 'Debit'],
  '1601': ['Greenhouse Structure', 'Asset', 'Debit'],
  '1602': ['Hydroponic Systems', 'Asset', 'Debit'],
  '1603': ['Solar & Electrical', 'Asset', 'Debit'],
  '1604': ['Equipment & Machinery', 'Asset', 'Debit'],
  '1610': ['Capital Work in Progress (CWIP)', 'Asset', 'Debit'],
  '1650': ['Accumulated Depreciation', 'Asset', 'Credit'],
  '2101': ['Supplier Payables', 'Liability', 'Credit'],
  '2201': ['Loans Payable', 'Liability', 'Credit'],
  '2401': ['Accrued Interest', 'Liability', 'Credit'],
  '2402': ['Accrued Salaries', 'Liability', 'Credit'],
  '3100': ['Share Capital', 'Equity', 'Credit'],
  '3201': ['Share Premium', 'Equity', 'Credit'],
  '3300': ['Capital Contributions', 'Equity', 'Credit'],
  '3400': ['Dividends & Withdrawals', 'Equity', 'Debit'],
  '3500': ['Retained Earnings', 'Equity', 'Credit'],
  '4101': ['Lettuce Sales — Grade A', 'Income', 'Credit'],
  '4102': ['Lettuce Sales — Grade B', 'Income', 'Credit'],
  '4103': ['Strawberry Sales — Grade A', 'Income', 'Credit'],
  '4104': ['Strawberry Sales — Grade B', 'Income', 'Credit'],
  '4105': ['Other Crop Sales', 'Income', 'Credit'],
  '4900': ['Other Income', 'Income', 'Credit'],
  '5100': ['Seeds & Seedlings', 'Expense', 'Debit'],
  '5200': ['Nutrients & Chemicals', 'Expense', 'Debit'],
  '5300': ['Growing Media', 'Expense', 'Debit'],
  '5400': ['Packaging Materials', 'Expense', 'Debit'],
  '5500': ['Direct Labour', 'Expense', 'Debit'],
  '5600': ['Cost of Goods Sold', 'Expense', 'Debit'],
  '6100': ['Management Salaries', 'Expense', 'Debit'],
  '6200': ['Electricity', 'Expense', 'Debit'],
  '6300': ['Fuel & Vehicle', 'Expense', 'Debit'],
  '6400': ['Equipment Repairs', 'Expense', 'Debit'],
  '6500': ['Rent', 'Expense', 'Debit'],
  '6600': ['Insurance', 'Expense', 'Debit'],
  '6700': ['Marketing & Delivery', 'Expense', 'Debit'],
  '6800': ['Professional Fees', 'Expense', 'Debit'],
  '6850': ['Technology & Software', 'Expense', 'Debit'],
  '6900': ['Bank Charges', 'Expense', 'Debit'],
  '7000': ['Depreciation', 'Expense', 'Debit'],
  '7100': ['Depreciation — Greenhouse', 'Expense', 'Debit'],
  '7200': ['Depreciation — Hydroponic Systems', 'Expense', 'Debit'],
  '7300': ['Depreciation — Solar & Electrical', 'Expense', 'Debit'],
  '8100': ['Interest Expense', 'Expense', 'Debit'],
  '9000': ['Miscellaneous Expenses', 'Expense', 'Debit'],
}

// Core function — posts a journal entry
export const postJournal = async ({
  journalId,
  date,
  module,
  reference,
  debitAccount,
  creditAccount,
  amount,
  description,
  sourceId,
  userId,
}) => {
  if (!amount || amount <= 0) return { error: 'Amount must be positive' }
  if (!debitAccount || !creditAccount) return { error: 'Both debit and credit accounts required' }
  if (debitAccount === creditAccount) return { error: 'Debit and credit accounts must differ' }

  // Auto-ensure both account codes exist in chart_of_accounts
  // Uses KNOWN_ACCOUNTS for names, falls back to code-range inference for unknowns
  for (const code of [debitAccount, creditAccount]) {
    if (KNOWN_ACCOUNTS[code]) {
      const [name, category, normalBalance] = KNOWN_ACCOUNTS[code]
      await ensureAccount(code, name, category, normalBalance)
    } else {
      // Infer category and normal balance from account code range
      const n = parseInt(code)
      const category = n < 2000 ? 'Asset' : n < 3000 ? 'Liability' : n < 4000 ? 'Equity' : n < 5000 ? 'Income' : 'Expense'
      const normalBalance = ['Liability', 'Equity', 'Income'].includes(category) ? 'Credit' : 'Debit'
      await ensureAccount(code, code, category, normalBalance)
    }
  }

  const { error } = await supabase.from('journal_entries').insert({
    journal_id: journalId || genJournalId(module.toUpperCase()),
    entry_date: date,
    module,
    reference: reference || null,
    debit_account: debitAccount,
    credit_account: creditAccount,
    amount: Math.round(parseFloat(amount)),
    description,
    source_id: sourceId ? String(sourceId) : null,
    created_by: userId || null,
  })
  return { error }
}

// ── LOAN JOURNALS ─────────────────────────────────────────────────────────────

// When a loan is received: Dr Cash / Cr Loan Payable
export const journalLoanReceived = async ({ loanId, lenderName, amount, date, userId }) => {
  return postJournal({
    journalId: `JNL-LOAN-${loanId}`,
    date, module: 'loan',
    reference: loanId,
    debitAccount: '1101',   // Cash at Bank
    creditAccount: '2201',  // Loans Payable
    amount,
    description: `Loan received — ${lenderName}`,
    sourceId: loanId,
    userId,
  })
}

// When a loan is repaid: Dr Loan Payable / Cr Cash
export const journalLoanRepayment = async ({ repaymentId, loanId, lenderName, principal, date, userId }) => {
  return postJournal({
    journalId: `JNL-REPAY-${repaymentId}`,
    date, module: 'loan_repayment',
    reference: repaymentId,
    debitAccount: '2201',  // Loans Payable
    creditAccount: '1101', // Cash at Bank
    amount: principal,
    description: `Loan repayment (principal) — ${lenderName}`,
    sourceId: repaymentId,
    userId,
  })
}

// Interest accrual: Dr Interest Expense / Cr Accrued Interest (NO CASH)
export const journalInterestAccrual = async ({ accrualId, lenderName, amount, date, userId }) => {
  return postJournal({
    journalId: `JNL-INT-${accrualId}`,
    date, module: 'interest',
    reference: accrualId,
    debitAccount: '8100',  // Interest Expense
    creditAccount: '2401', // Accrued Interest Payable
    amount,
    description: `Interest accrual — ${lenderName}`,
    sourceId: accrualId,
    userId,
  })
}

// Interest paid: Dr Accrued Interest / Cr Cash
export const journalInterestPayment = async ({ repaymentId, lenderName, interest, date, userId }) => {
  return postJournal({
    journalId: `JNL-INTPAY-${repaymentId}`,
    date, module: 'interest_payment',
    reference: repaymentId,
    debitAccount: '2401',  // Accrued Interest Payable
    creditAccount: '1101', // Cash at Bank
    amount: interest,
    description: `Interest payment — ${lenderName}`,
    sourceId: repaymentId,
    userId,
  })
}

// ── EQUITY JOURNALS ───────────────────────────────────────────────────────────

// Share capital issued but unpaid: Dr Shareholder Receivable / Cr Share Capital
export const journalShareCapital = async ({ amount, date, notes, userId }) => {
  return postJournal({
    journalId: 'JNL-SHARECAP-001',
    date, module: 'equity',
    reference: 'SHARE-CAP',
    debitAccount: '1220',  // Shareholder Receivable
    creditAccount: '3100', // Share Capital
    amount,
    description: notes || 'Share capital issued (unpaid)',
    userId,
  })
}

// Capital contribution (cash injected): Dr Cash / Cr Capital Contributions
export const journalCapitalContribution = async ({ entryId, contributor, amount, date, userId }) => {
  return postJournal({
    journalId: `JNL-CAP-${entryId}`,
    date, module: 'equity',
    reference: entryId,
    debitAccount: '1101',  // Cash at Bank
    creditAccount: '3300', // Capital Contributions
    amount,
    description: `Capital contribution — ${contributor}`,
    sourceId: entryId,
    userId,
  })
}

// Owner withdrawal: Dr Withdrawals / Cr Cash
export const journalOwnerWithdrawal = async ({ entryId, contributor, amount, date, userId }) => {
  return postJournal({
    journalId: `JNL-WITH-${entryId}`,
    date, module: 'equity',
    reference: entryId,
    debitAccount: '3400',  // Dividends & Withdrawals
    creditAccount: '1101', // Cash at Bank
    amount,
    description: `Owner withdrawal — ${contributor}`,
    sourceId: entryId,
    userId,
  })
}

// ── SUPPLIER JOURNALS ─────────────────────────────────────────────────────────

// Supplier invoice received: Dr Expense/Asset / Cr Supplier Payables
export const journalSupplierInvoice = async ({ invoiceId, supplier, amount, expenseAccount, date, userId }) => {
  return postJournal({
    journalId: `JNL-SI-${invoiceId}`,
    date, module: 'supplier_invoice',
    reference: invoiceId,
    debitAccount: expenseAccount || '5200', // default to Nutrients & Chemicals
    creditAccount: '2101', // Supplier Payables
    amount,
    description: `Supplier invoice — ${supplier}`,
    sourceId: invoiceId,
    userId,
  })
}

// Supplier payment: Dr Supplier Payables / Cr Cash
export const journalSupplierPayment = async ({ paymentId, supplier, amount, date, userId }) => {
  return postJournal({
    journalId: `JNL-PAY-${paymentId}`,
    date, module: 'payables',
    reference: paymentId,
    debitAccount: '2101',  // Supplier Payables
    creditAccount: '1101', // Cash at Bank
    amount,
    description: `Supplier payment — ${supplier}`,
    sourceId: paymentId,
    userId,
  })
}

// ── FIXED ASSET JOURNALS ──────────────────────────────────────────────────────

// Asset purchased — cash portion: Dr CWIP / Cr Cash
export const journalAssetPurchaseCash = async ({ assetId, assetName, amount, date, userId }) => {
  return postJournal({
    journalId: `JNL-FA-PAID-${assetId}`,
    date, module: 'fixed_asset',
    reference: assetId,
    debitAccount: '1610',  // CWIP
    creditAccount: '1101', // Cash at Bank
    amount,
    description: `Asset purchase (cash) — ${assetName}`,
    sourceId: assetId,
    userId,
  })
}

// Asset purchased — outstanding portion: Dr CWIP / Cr Payables
export const journalAssetPurchasePayable = async ({ assetId, assetName, amount, date, userId }) => {
  return postJournal({
    journalId: `JNL-FA-PAY-${assetId}`,
    date, module: 'fixed_asset',
    reference: assetId,
    debitAccount: '1610',  // CWIP
    creditAccount: '2101', // Supplier Payables
    amount,
    description: `Asset payable — ${assetName}`,
    sourceId: assetId,
    userId,
  })
}

// Asset capitalised (CWIP → Fixed Asset): Dr Fixed Asset Account / Cr CWIP
export const journalAssetCapitalised = async ({ assetId, assetName, amount, assetAccount, date, userId }) => {
  return postJournal({
    journalId: `JNL-FA-CAP-${assetId}`,
    date, module: 'fixed_asset_capitalise',
    reference: assetId,
    debitAccount: assetAccount || '1602', // Fixed Asset account
    creditAccount: '1610', // CWIP
    amount,
    description: `Asset capitalised — ${assetName}`,
    sourceId: assetId,
    userId,
  })
}

// Monthly depreciation: Dr Depreciation Expense / Cr Accumulated Depreciation
export const journalDepreciation = async ({ assetId, assetName, amount, expenseAccount, date, userId }) => {
  return postJournal({
    journalId: `JNL-DEP-${assetId}-${date}`,
    date, module: 'depreciation',
    reference: assetId,
    debitAccount: expenseAccount || '7000', // Depreciation Expense
    creditAccount: '1650', // Accumulated Depreciation
    amount,
    description: `Depreciation — ${assetName}`,
    sourceId: assetId,
    userId,
  })
}

// ── REVENUE JOURNALS ──────────────────────────────────────────────────────────

// Customer invoice raised: Dr Trade Receivable / Cr Revenue
export const journalInvoiceRaised = async ({ invoiceId, invoiceNumber, customer, amount, revenueAccount, date, userId }) => {
  return postJournal({
    journalId: `JNL-INV-${invoiceId}`,
    date, module: 'invoice',
    reference: invoiceNumber,
    debitAccount: '1201',  // Trade Receivables
    creditAccount: revenueAccount || '4105', // Revenue
    amount,
    description: `Invoice raised — ${customer}`,
    sourceId: invoiceId,
    userId,
  })
}

// Customer payment received: Dr Cash / Cr Trade Receivable
export const journalCustomerPayment = async ({ paymentId, invoiceId, customer, amount, date, userId }) => {
  return postJournal({
    journalId: `JNL-CPAY-${paymentId}`,
    date, module: 'customer_payment',
    reference: invoiceId,
    debitAccount: '1101',  // Cash at Bank
    creditAccount: '1201', // Trade Receivables
    amount,
    description: `Customer payment — ${customer}`,
    sourceId: paymentId,
    userId,
  })
}

// ── INVENTORY JOURNALS ────────────────────────────────────────────────────────

// Purchase inventory: Dr Raw Materials / Cr Cash or Payable
export const journalInventoryPurchase = async ({ refId, item, amount, date, payOnCredit, userId }) => {
  return postJournal({
    journalId: `JNL-INV-PURCH-${refId}`,
    date, module: 'inventory',
    reference: refId,
    debitAccount: '1310',  // Raw Materials
    creditAccount: payOnCredit ? '2101' : '1101',
    amount,
    description: `Inventory purchase — ${item}`,
    sourceId: refId,
    userId,
  })
}

// Production (Raw → WIP): Dr WIP / Cr Raw Materials
export const journalRawToWip = async ({ refId, description, amount, date, userId }) => {
  return postJournal({
    journalId: `JNL-WIP-${refId}`,
    date, module: 'inventory_wip',
    reference: refId,
    debitAccount: '1320',  // WIP
    creditAccount: '1310', // Raw Materials
    amount,
    description: description || 'Production — Raw to WIP',
    sourceId: refId,
    userId,
  })
}

// Completion (WIP → Finished Goods): Dr Finished Goods / Cr WIP
export const journalWipToFinished = async ({ refId, description, amount, date, userId }) => {
  return postJournal({
    journalId: `JNL-FG-${refId}`,
    date, module: 'inventory_finished',
    reference: refId,
    debitAccount: '1330',  // Finished Goods
    creditAccount: '1320', // WIP
    amount,
    description: description || 'Completion — WIP to Finished Goods',
    sourceId: refId,
    userId,
  })
}

// COGS on sale: Dr COGS / Cr Finished Goods
export const journalCOGS = async ({ invoiceId, amount, date, userId }) => {
  return postJournal({
    journalId: `JNL-COGS-${invoiceId}`,
    date, module: 'cogs',
    reference: invoiceId,
    debitAccount: '5600',  // COGS
    creditAccount: '1330', // Finished Goods
    amount,
    description: 'Cost of goods sold',
    sourceId: invoiceId,
    userId,
  })
}

// ── PAYROLL JOURNALS ──────────────────────────────────────────────────────────

// Salary payment: Dr Salary Expense / Cr Cash (+ Cr Statutory Payables for deductions)
export const journalSalaryPayment = async ({ refId, employee, grossSalary, netPay, paye, nssf, date, userId }) => {
  const jId = `JNL-SAL-${refId}`
  const errors = []

  // Net pay: Dr Salary Expense / Cr Cash
  const r1 = await postJournal({
    journalId: jId, date, module: 'payroll', reference: refId,
    debitAccount: '6100', creditAccount: '1101',
    amount: netPay,
    description: `Salary payment — ${employee}`,
    sourceId: refId, userId,
  })
  if (r1.error) errors.push(r1.error)

  // PAYE: Dr Salary Expense / Cr PAYE Payable
  if (paye > 0) {
    const r2 = await postJournal({
      journalId: jId, date, module: 'payroll', reference: refId,
      debitAccount: '6100', creditAccount: '2301',
      amount: paye,
      description: `PAYE — ${employee}`,
      sourceId: refId, userId,
    })
    if (r2.error) errors.push(r2.error)
  }

  // NSSF: Dr Salary Expense / Cr NSSF Payable
  if (nssf > 0) {
    const r3 = await postJournal({
      journalId: jId, date, module: 'payroll', reference: refId,
      debitAccount: '6100', creditAccount: '2302',
      amount: nssf,
      description: `NSSF — ${employee}`,
      sourceId: refId, userId,
    })
    if (r3.error) errors.push(r3.error)
  }

  return { error: errors.length > 0 ? errors.join(', ') : null }
}

// ── CASH EXPENSE JOURNALS ─────────────────────────────────────────────────────

// Map expense types to account codes
export const EXPENSE_ACCOUNT_MAP = {
  'Seeds & Seedlings': '5100',
  'Nutrients & Chemicals': '5200',
  'Growing Media': '5300',
  'Packaging Materials': '5400',
  'Direct Labour': '5500',
  'Management Salaries': '6100',
  'Electricity': '6200',
  'Electricity - General': '6200',
  'Electricity - Growing': '6200',
  'Fuel & Vehicle': '6300',
  'Equipment Repairs & Maintenance': '6400',
  'Rent': '6500',
  'Insurance': '6600',
  'Marketing & Delivery': '6700',
  'Professional Fees': '6800',
  'Bank Charges': '6900',
  'Interest Expense': '8100',
  'Loan Repayment': '2201',
  'default': '9000',
}

// Direct cash expense: Dr Expense / Cr Cash
export const journalCashExpense = async ({ refId, description, category, amount, date, userId }) => {
  const expenseAccount = EXPENSE_ACCOUNT_MAP[category] || EXPENSE_ACCOUNT_MAP['default']
  return postJournal({
    journalId: `JNL-EXP-${refId}`,
    date, module: 'cash_expense',
    reference: refId,
    debitAccount: expenseAccount,
    creditAccount: '1101', // Cash at Bank
    amount,
    description: description || category,
    sourceId: refId,
    userId,
  })
}

// ── QUERY HELPERS ─────────────────────────────────────────────────────────────

// Get trial balance as of a date
export const getTrialBalance = async (asOfDate) => {
  const { data, error } = await supabase
    .from('trial_balance')
    .select('*')
  return { data, error }
}

// Get account balance for a specific account code
export const getAccountBalance = async (accountCode, asOfDate) => {
  let query = supabase.from('journal_entries')
    .select('debit_account, credit_account, amount')
    .or(`debit_account.eq.${accountCode},credit_account.eq.${accountCode}`)
  if (asOfDate) query = query.lte('entry_date', asOfDate)
  const { data, error } = await query
  if (error) return { balance: 0, error }
  const debits = data.filter(e => e.debit_account === accountCode).reduce((s, e) => s + e.amount, 0)
  const credits = data.filter(e => e.credit_account === accountCode).reduce((s, e) => s + e.amount, 0)
  return { debits, credits, balance: debits - credits, error: null }
}

// Get all balances for a category
export const getCategoryBalances = async (category, asOfDate) => {
  const { data: accounts } = await supabase.from('chart_of_accounts')
    .select('code').eq('category', category).eq('is_active', true)
  const codes = (accounts || []).map(a => a.code)
  let query = supabase.from('journal_entries').select('debit_account, credit_account, amount')
    .or(codes.map(c => `debit_account.eq.${c},credit_account.eq.${c}`).join(','))
  if (asOfDate) query = query.lte('entry_date', asOfDate)
  const { data } = await query
  const balances = {}
  codes.forEach(code => {
    const entries = (data || []).filter(e => e.debit_account === code || e.credit_account === code)
    const debits = entries.filter(e => e.debit_account === code).reduce((s, e) => s + e.amount, 0)
    const credits = entries.filter(e => e.credit_account === code).reduce((s, e) => s + e.amount, 0)
    balances[code] = { debits, credits, balance: debits - credits }
  })
  return balances
}
